import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { FuseConfigService } from '@fuse/services/config';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { LanguagePreferenceService } from 'app/core/transloco/language-preference.service';
import { environment } from 'environments/environment';
import { BehaviorSubject, catchError, map, Observable, of, ReplaySubject, shareReplay, tap, throwError } from 'rxjs';
import { setModulePermissionSnapshot } from './module-permission-snapshot';
import { RecordAction, RecordScope, defaultRecordAction, resolveRecordAction, resolveRecordScope } from './record-scope';
import {
    Company,
    MemberStaffProfile,
    MemberStaffProfileUpdate,
    OwnerPersonalProfile,
    QuickTourProgress,
    ReportTheme,
} from './user.types';
import { normalizeAvatarUrl } from './avatar-url.util';
import { readTourCompletedFromAuthPayload } from './tour-completed.util';

/** Mirrors backend `userService.getPerms` when `role === Role.admin` - not fetched via HTTP for super admins. */
type PermissionPayload = Record<string, any>;

const SUPER_ADMIN_PERMISSIONS: PermissionPayload = {
    name: 'Super Admin',
    subscriptionId: 0,
    email: '',
    agents: 'full_access',
    custom_agents: 'full_access',
    hyper_forms: 'full_access',
    sai_chat: 'full_access',
    generated_report: 'full_access',
    trackers: 'full_access',
    activities: 'full_access',
    procedures: 'full_access',
    voice_chat: 'full_access',
    tasks: 'full_access',
    group_chat: 'full_access',
    data_source: 'full_access',
    // The calibration workspace and the data-quality checks that feed it share one feature key
    // (backend `plugins/permission`: both `/cognition/` and `/data-quality/` map to `cognition`),
    // because the sensor and the workspace are one capability and either half alone is broken.
    cognition: 'full_access',
    // The automations module. It is listed in `MODULE_PERMISSION_KEYS` below but was missing here,
    // so a platform super admin normalised to `automation: false` and lost a module they are
    // supposed to hold in full. Every key in that list needs a counterpart in this object.
    automation: 'full_access',
    // The CRM module (leads, visits, pipelines, ERP sync). One key covers both the `/crm/` and
    // `/crm-sync/` backend prefixes.
    crm: 'full_access',
    // Messaging channels: connections, the contact directory, and the agent tools that reach
    // somebody outside the product (workspace ADR 0016). Every key in `MODULE_PERMISSION_KEYS`
    // below needs a counterpart here, or a platform super admin normalises to `false` and loses a
    // module they are supposed to hold in full.
    channels: 'full_access',
    // The approvals inbox. Listed for the same reason every key above it is: a key in
    // `MODULE_PERMISSION_KEYS` with no counterpart here normalises to `false` for a platform super
    // admin, who would then be bounced off a module they hold in full.
    approvals: 'full_access',
    // The dashboards module (workspace ADR 0021). Listed for the same reason every key above it is:
    // a key in `MODULE_PERMISSION_KEYS` with no counterpart here normalises to `false` for a
    // platform super admin, who would then be bounced off a module they hold in full.
    dashboards: 'full_access',
    harvester: 'full_access',
    // Platform super admins are never narrowed — same as the account-owner root (ADR 0007).
    record_scopes: { crm: 'all' },
    record_actions: { crm: 'delete' },
    max_iteration: 0,
};

/**
 * Permission keys returned by `users/get-perms`, normalised to booleans for the UI.
 *
 * Exported because the sidebar's visibility check is a STRICT boolean compare
 * (`this.permissions?.[item.permissionKey] === true` in
 * `@fuse/components/navigation/vertical/vertical.component.ts`), so every `permissionKey` used in
 * `mock-api/common/navigation/data.ts` has to appear here or the payload's `'full_access'` level
 * string never equals `true` and the item stays hidden from the people who hold it.
 * `data.spec.ts` pins that cross-check for the whole navigation tree.
 */
export const MODULE_PERMISSION_KEYS = [
    'agents',
    'custom_agents',
    'hyper_forms',
    'sai_chat',
    'generated_report',
    'trackers',
    'activities',
    'voice_chat',
    'procedures',
    'tasks',
    'group_chat',
    'data_source',
    // The automations module. Absent from this list, the normaliser left it as whatever the payload
    // carried, and nothing that reads a boolean would ever see it - which is how the nav item came
    // to show for a tenant the backend refuses.
    'automation',
    // The calibration workspace (Cognition Mode). Listed here for the same reason `automation` is:
    // a key absent from this list is left as whatever the payload carried, so nothing reading a
    // boolean ever sees it and the gate silently passes.
    'cognition',
    // The CRM module. Unlike `automation` and `cognition` above, this one was added BEFORE it could
    // bite: all three readers of the payload (`hasModulePermission`, `PermissionGuard`,
    // `isPermissionEnabled`) now normalise the raw level string and reject `'disabled'`, so an
    // unlisted key no longer leaks. It is listed anyway so the payload's contract stays true and a
    // future reader doing a plain truthiness check cannot reintroduce the bug.
    'crm',
    // Messaging channels. Listed for the same reason `crm` is: a key absent from this list is left
    // as whatever the payload carried, so nothing reading a boolean ever sees it. Nothing gates a
    // route on it today — the Channels nav item is role-gated (Organization Admin only) and the
    // `/v1/channels/*` routes are unchanged — so listing it keeps the payload contract true rather
    // than changing what is visible.
    'channels',
    // The approvals inbox. The nav item gates on this key and the payload sends it as a level
    // string, so without this line `permissions['approvals']` stays `'full_access'`, the sidebar's
    // `=== true` compare fails, and the item is invisible to every entitled user while the route
    // still opens on a deep link - a half-working module that looks like a missing feature.
    'approvals',
    // The dashboards module. Listed before the feature has any frontend routes so the payload
    // contract is true from the first request: `PermissionGuard` gates `/dashboards` on this key,
    // and the guard's normaliser rejects the `'disabled'` level the backend stores for every
    // existing tenant. A nav item will gate on the same key when the module has somewhere to go.
    'dashboards',
    // Harvester price audits (workspace ADR 0026); gates `/harvester` and its nav item.
    'harvester',
] as const;

/** Extra payload spellings accepted for a given key. */
const MODULE_PERMISSION_ALIASES: Partial<Record<string, string[]>> = {
    data_source: ['data_source', 'data_sources'],
};

function normalizePermissionFlag(value: unknown): boolean {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value > 0;

    const normalized = String(value ?? '')
        .trim()
        .toLowerCase();

    if (!normalized) return false;

    return normalized === 'full_access' || normalized === 'read_only';
}

@Injectable({ providedIn: 'root' })
export class UserService {
    private apiLink = environment.apiLink;
    private _httpClient = inject(HttpClient);
    private _languagePreferenceService = inject(LanguagePreferenceService);
    private _user: ReplaySubject<Company> = new ReplaySubject<Company>(1);
    private _company: ReplaySubject<Company> = new ReplaySubject<Company>(1);
    /**
     * Shared in-flight / completed `GET users/profile` (via `getcompany()`).
     * Many boot-time callers (layout, overview, quick-tour, user menu) used to fire the same
     * request in parallel — `shareReplay(1)` collapses them to one XHR per session until cleared.
     */
    private _companyProfileRequest$: Observable<Company> | null = null;
    /** Member/staff personal avatar URL (drives the toolbar avatar for `manager`/`staff`). */
    private _memberStaffPhoto: BehaviorSubject<string> = new BehaviorSubject<string>(
        normalizeAvatarUrl(localStorage.getItem('user_image'))
    );
    /** Cached member/staff profile — eagerly loaded on login and layout init (not only on `/pages/my-profile`). */
    private _memberStaffProfile: BehaviorSubject<MemberStaffProfile | null> =
        new BehaviorSubject<MemberStaffProfile | null>(null);

    constructor(private _fuseConfigService: FuseConfigService) {}

    set user(value: Company) {
        this._user.next(value);
    }

    get user$(): Observable<Company> {
        return this._user.asObservable();
    }

    set company(value: Company) {
        this._company.next(value);
    }

    get company$(): Observable<Company> {
        return this._company.asObservable();
    }

    /** Derived observable: emits the company profile photo URL whenever the company stream updates. */
    get companyPhoto(): Observable<string> {
        return this._company.pipe(map((c: any) => normalizeAvatarUrl(c?.image)));
    }

    /** Derived observable: emits the company display name whenever the company stream updates. */
    get companyName(): Observable<string> {
        return this._company.pipe(map((c: any) => c?.company_name ?? ''));
    }

    /** Derived observable: emits the company email whenever the company stream updates. */
    get companyEmail(): Observable<string> {
        return this._company.pipe(map((c: any) => c?.email ?? ''));
    }

    /** Derived observable: emits the company id whenever the company stream updates. */
    get companyId(): Observable<string> {
        return this._company.pipe(map((c: any) => c?.id ?? ''));
    }

    /** Derived observable: emits the company type whenever the company stream updates. */
    get companyType(): Observable<string> {
        return this._company.pipe(map((c: any) => c?.company_type ?? ''));
    }

    /** Derived observable: emits the company brand color (`primary_color`, not the UI theme) whenever the company stream updates. */
    get companyColor(): Observable<string> {
        return this._company.pipe(map((c: any) => c?.primary_color ?? ''));
    }

    /** Derived observable: emits the company style/menu style whenever the company stream updates. */
    get companyMenuStyle(): Observable<string> {
        return this._company.pipe(map((c: any) => c?.company_style ?? ''));
    }

    /** Derived observable: emits the company theme whenever the company stream updates. */
    get companyTheme(): Observable<string> {
        return this._company.pipe(map((c: any) => c?.theme ?? ''));
    }

    /**
     * Derived observable: the organization default theme for generated reports.
     * Falls back to light, matching the backend default, so a payload from an older
     * session never leaves the report dialog without a selection.
     */
    get defaultReportTheme$(): Observable<ReportTheme> {
        return this._company.pipe(
            map((c: Company) =>
                c?.default_report_theme === 'dark' ? 'dark' : 'light'
            )
        );
    }

    private _selectedLang: BehaviorSubject<string> = new BehaviorSubject<string>('en');

    setActiveLang(value: string) {
        this._selectedLang.next(value);
    }

    get selectedLang$() {
        return this._selectedLang.asObservable();
    }

    private _permissionsCache: PermissionPayload | null = null;
    /** Raw level per module key (`full_access` / `read_only` / `disabled`), kept beside the boolean cache. */
    private _permissionLevels: Record<string, string> = {};
    /**
     * Session mirror of backend `tourCompleted`.
     * Needed because skip/finish updates the server, but the access JWT keeps the old claim until
     * the next login/refresh — so a page reload would re-show the tour without a durable local copy.
     */
    private static readonly TOUR_COMPLETED_STORAGE_KEY = 'tourCompleted';
    private _tourCompleted: boolean | null = null;

    /** True when the signed-in user has finished (or skipped) the org-admin SAI Quick Tour. */
    isQuickTourCompleted(): boolean {
        if (this._tourCompleted !== null) {
            return this._tourCompleted;
        }

        const fromStorage = this._readTourCompletedFromStorage();
        if (fromStorage !== null) {
            this._tourCompleted = fromStorage;
            return fromStorage;
        }

        const fromToken = this._readTourCompletedFromAccessToken();
        if (fromToken !== null) {
            this._setTourCompleted(fromToken);
            return fromToken;
        }

        return false;
    }

    /** Apply `tourCompleted` from login, refresh-token, or profile payloads (source of truth). */
    applyTourCompletedFromAuth(value: unknown): void {
        const completed =
            typeof value === 'boolean'
                ? value
                : readTourCompletedFromAuthPayload(value);
        if (typeof completed === 'boolean') {
            this._setTourCompleted(completed);
        }
    }

    /**
     * Refresh `tourCompleted` from `GET users/profile` before auto-starting the Quick Tour.
     * Login/refresh may have already set it; profile is the durable source on page reload.
     * Reuses `getcompany()` so this does not fire a second profile XHR beside layout/overview.
     */
    syncQuickTourCompletedFromProfile(): Observable<void> {
        if (!this._hasValidAccessToken()) {
            return of(undefined);
        }

        return this.getcompany().pipe(
            map(() => undefined),
            catchError(() => of(undefined)),
        );
    }

    /** Persist tour completion to the backend and update the session flag (survives refresh). */
    markQuickTourCompleted(): Observable<void> {
        this._setTourCompleted(true);

        if (!this._hasValidAccessToken()) {
            return of(undefined);
        }

        return this._httpClient
            .put<{ done?: boolean; tourCompleted?: boolean; data?: { tourCompleted?: boolean } }>(
                `${this.apiLink}users/profile/tour-completed`,
                {},
            )
            .pipe(
                map((response) => {
                    const completed =
                        response?.tourCompleted ??
                        response?.data?.tourCompleted;
                    if (typeof completed === 'boolean') {
                        this._setTourCompleted(completed);
                    }
                    return undefined;
                }),
                catchError(() => of(undefined)),
            );
    }

    clearTourCompletedCache(): void {
        this._tourCompleted = null;
        localStorage.removeItem(UserService.TOUR_COMPLETED_STORAGE_KEY);
        this.clearQuickTourProgress();
    }

    /**
     * Resume point of an interrupted Quick Tour run.
     * Stored because closing the tour (X / Esc) or refreshing the page is not completion — the
     * next run has to open on the step the user left, and only finish/skip clears it.
     */
    private static readonly TOUR_PROGRESS_STORAGE_KEY = 'saiQuickTourProgress';

    readQuickTourProgress(): QuickTourProgress | null {
        const raw = localStorage.getItem(UserService.TOUR_PROGRESS_STORAGE_KEY);
        if (!raw) {
            return null;
        }

        try {
            const parsed = JSON.parse(raw) as Partial<QuickTourProgress> | null;
            if (
                !parsed ||
                typeof parsed.stepId !== 'string' ||
                typeof parsed.stepIndex !== 'number'
            ) {
                return null;
            }
            return { stepId: parsed.stepId, stepIndex: parsed.stepIndex };
        } catch {
            return null;
        }
    }

    saveQuickTourProgress(progress: QuickTourProgress): void {
        localStorage.setItem(
            UserService.TOUR_PROGRESS_STORAGE_KEY,
            JSON.stringify(progress),
        );
    }

    clearQuickTourProgress(): void {
        localStorage.removeItem(UserService.TOUR_PROGRESS_STORAGE_KEY);
    }

    private _setTourCompleted(value: boolean): void {
        this._tourCompleted = value;
        localStorage.setItem(UserService.TOUR_COMPLETED_STORAGE_KEY, value ? '1' : '0');
    }

    private _readTourCompletedFromStorage(): boolean | null {
        const raw = localStorage.getItem(UserService.TOUR_COMPLETED_STORAGE_KEY);
        if (raw === '1') {
            return true;
        }
        if (raw === '0') {
            return false;
        }
        return null;
    }

    private _hasValidAccessToken(): boolean {
        const token = localStorage.getItem('accessToken');
        return !!token && !AuthUtils.isTokenExpired(token);
    }

    private _readTourCompletedFromAccessToken(): boolean | null {
        const token = localStorage.getItem('accessToken');
        if (!token) {
            return null;
        }

        const payload = AuthUtils.decodeTokenPayload(token);
        if (!payload || typeof payload['tourCompleted'] !== 'boolean') {
            return null;
        }

        return payload['tourCompleted'];
    }

    /** Super admin (`userRole` from login) is not a tenant user; skip get-perms (may 401) and use full access locally. */
    private isSuperAdmin(): boolean {
        return localStorage.getItem('userRole') === 'admin';
    }

    private extractPermissionsPayload(response: any): PermissionPayload {
        const base =
            response?.data && typeof response.data === 'object'
                ? response.data
                : response;

        if (!base || typeof base !== 'object') {
            return {};
        }

        const raw = { ...(base as PermissionPayload) };
        if (raw['permissions'] && typeof raw['permissions'] === 'object') {
            return {
                ...raw,
                ...(raw['permissions'] as PermissionPayload),
            };
        }

        return raw;
    }

    private normalizePermissionsPayload(response: any): PermissionPayload {
        const raw = this.extractPermissionsPayload(response);
        const normalized: PermissionPayload = { ...raw };

        for (const key of MODULE_PERMISSION_KEYS) {
            const candidates = MODULE_PERMISSION_ALIASES[key] ?? [key];
            const matchedKey = candidates.find(
                candidate => raw[candidate] !== undefined && raw[candidate] !== null
            );
            normalized[key] = matchedKey ? normalizePermissionFlag(raw[matchedKey]) : false;
            const level = matchedKey ? raw[matchedKey] : undefined;
            this._permissionLevels[key] = typeof level === 'string' ? level : level === true ? 'full_access' : 'disabled';
        }

        // Keep `record_scopes` as a nested map (never coerce to a boolean module flag).
        if (raw['record_scopes'] && typeof raw['record_scopes'] === 'object') {
            normalized['record_scopes'] = { ...(raw['record_scopes'] as Record<string, unknown>) };
        }
        if (raw['record_actions'] && typeof raw['record_actions'] === 'object') {
            normalized['record_actions'] = { ...(raw['record_actions'] as Record<string, unknown>) };
        }

        return normalized;
    }

    /**
     * Effective record scope for a feature (`own` / `team` / `all`).
     * Company / platform admin default to `all` when the payload omits the key; members default to `own`.
     */
    getRecordScope(featureKey: string): RecordScope {
        const role = localStorage.getItem('userRole');
        const fallback: RecordScope =
            role === 'company' || role === 'admin' ? 'all' : 'own';
        return resolveRecordScope(this._permissionsCache, featureKey, fallback);
    }

    /** True when the caller can see records beyond their own for this feature. */
    canSeeTeamRecords(featureKey = 'crm'): boolean {
        return this.getRecordScope(featureKey) !== 'own';
    }

    /**
     * What the caller may do with another person's record in this feature (`view` / `edit` /
     * `delete`). Company / platform admin are never narrowed; a member or staff row without a
     * stored value gets the backend's per-role default.
     */
    getRecordAction(featureKey = 'crm'): RecordAction {
        const role = localStorage.getItem('userRole');
        if (role === 'company' || role === 'admin') return 'delete';
        const fallback = defaultRecordAction(role === 'staff' ? 'staff' : 'manager');
        return resolveRecordAction(this._permissionsCache, featureKey, fallback);
    }

    getUserPermissions(): Observable<PermissionPayload> {
        if (this.isSuperAdmin()) {
            const data = this.normalizePermissionsPayload(SUPER_ADMIN_PERMISSIONS);
            this._permissionsCache = data;
            setModulePermissionSnapshot(data);
            return of(data);
        }
        return this._httpClient
            .get<any>(this.apiLink + 'users/get-perms')
            .pipe(
                map(response => this.normalizePermissionsPayload(response)),
                tap((data) => {
                    this._permissionsCache = data;
                    // Navigation visibility is decided by a SYNCHRONOUS predicate that cannot await
                    // this call; see `module-permission-snapshot.ts`.
                    setModulePermissionSnapshot(data);
                })
            );
    }

    /** Returns permissions for guards; uses cache after first load to avoid repeated API calls. */
    getPermissionsForGuard(): Observable<PermissionPayload> {
        if (this._permissionsCache != null) {
            return of(this._permissionsCache);
        }
        return this.getUserPermissions();
    }

    /**
     * The caller's level for one module key. The boolean payload says whether the module opens at all;
     * this says whether it opens read only, so a page can hide actions the backend would refuse.
     */
    getModuleAccessLevel(key: string): Observable<'full_access' | 'read_only' | 'disabled'> {
        return this.getPermissionsForGuard().pipe(
            map(() => {
                const level = this._permissionLevels[key];
                return level === 'full_access' || level === 'read_only' ? level : 'disabled';
            })
        );
    }

    /** Clear permissions cache (e.g. on logout). */
    clearPermissionsCache(): void {
        this._permissionsCache = null;
        this._permissionLevels = {};
        // Cleared with the cache, not separately: a stale snapshot would show the previous account's
        // modules to whoever signs in next on this browser.
        setModulePermissionSnapshot(null);
        this._memberStaffProfile.next(null);
        this.clearCompanyProfileCache();
        this.clearTourCompletedCache();
    }

    /** Drop the shared `GET users/profile` cache so the next `getcompany()` hits the network. */
    clearCompanyProfileCache(): void {
        this._companyProfileRequest$ = null;
    }

    checkIntegration() {
        return this._httpClient.get(this.apiLink + 'users/check-integrations');
    }

    updateIntegrationsSnowflake(status): Observable<any> {
        const params = new HttpParams().append('status', status);
        return this._httpClient.put(
            this.apiLink + 'users/update-integrations-snowflake',
            {},
            { params }
        );
    }

    updateIntegrationsSap(status): Observable<any> {
        const params = new HttpParams().append('status', status);
        return this._httpClient.put(
            this.apiLink + 'users/update-integrations-sap',
            {},
            { params }
        );
    }

    updateIntegrationsOracle(status): Observable<any> {
        const params = new HttpParams().append('status', status);
        return this._httpClient.put(
            this.apiLink + 'users/update-integrations-oracle',
            {},
            { params }
        );
    }

    updateIntegrationsPostgresql(status): Observable<any> {
        const params = new HttpParams().append('status', status);
        return this._httpClient.put(
            this.apiLink + 'users/update-integrations-remote-postgres',
            {},
            { params }
        );
    }

    /**
     * Load company profile once and share the result (`shareReplay(1)`).
     * Pass `forceRefresh: true` (or call `clearCompanyProfileCache()`) when the caller needs a
     * fresh network read — e.g. after an out-of-band mutation that did not go through `editcompany`.
     */
    getcompany(forceRefresh = false): Observable<Company> {
        if (forceRefresh) {
            this.clearCompanyProfileCache();
        }
        if (!this._companyProfileRequest$) {
            this._companyProfileRequest$ = this._httpClient
                .get<{ data: { user: Company & { prefLang?: string | null } } }>(
                    this.apiLink + 'users/profile'
                )
                .pipe(
                    map((company) => {
                        const user = company.data.user;
                        this._languagePreferenceService.applyFromBackend(
                            user?.prefLang ?? user?.pref_lang
                        );
                        this.applyTourCompletedFromAuth(company);
                        localStorage.setItem(
                            'currency_code',
                            user?.currency ? user.currency : 'SAR'
                        );
                        this._company.next(user);
                        this._user.next(user);
                        return user;
                    }),
                    catchError((err) => {
                        this.clearCompanyProfileCache();
                        return throwError(() => err);
                    }),
                    shareReplay({ bufferSize: 1, refCount: false })
                );
        }
        return this._companyProfileRequest$;
    }

    getcurrency(): Observable<any> {
        return this._httpClient.get<any>(this.apiLink + 'users/currency').pipe(
            map((company) => {
                return company.data.currency;
            })
        );
    }

    editcompany(company: any): Observable<any> {
        return this._httpClient
            .put<any>(this.apiLink + 'users/profile', company)
            .pipe(
                map((response) => {
                    this.clearCompanyProfileCache();
                    this._company.next(response.data.user);
                    this._user.next(response.data.user);
                    return of(response.data.user);
                })
            );
    }

    private syncUserAfterMemberStaffProfile(response: any): void {
        const user = response?.data?.user ?? response?.data;
        if (!user || typeof user !== 'object') {
            return;
        }
        this.clearCompanyProfileCache();
        this._company.next(user);
        this._user.next(user);
        if (user.email) {
            localStorage.setItem('email', user.email);
        }
        const displayName = user.acc_name ?? user.user_name ?? user.username;
        if (displayName) {
            localStorage.setItem('user_name', displayName);
        }
    }

    editcompanyData(company: any): Observable<any> {
        const role = localStorage.getItem('userRole') ?? '';
        // Email is read-only for member/staff self-service; only the display name is editable here.
        const memberStaffBody = {
            username: company.name,
        };
        if (role === 'manager') {
            return this._httpClient
                .put<any>(this.apiLink + 'members/profile', memberStaffBody)
                .pipe(
                    tap(response =>
                        this.syncUserAfterMemberStaffProfile(response)
                    )
                );
        }
        if (role === 'staff') {
            return this._httpClient
                .put<any>(this.apiLink + 'staff/profile', memberStaffBody)
                .pipe(
                    tap(response =>
                        this.syncUserAfterMemberStaffProfile(response)
                    )
                );
        }
        return this._httpClient
            .put<any>(this.apiLink + 'users/profile-data', company)
            .pipe(
                map((response) => {
                    this.clearCompanyProfileCache();
                    this._company.next(response.data.user);
                    this._user.next(response.data.user);
                    return of(response.data.user);
                })
            );
    }

    /**
     * The account owner's own details, as a PERSON (`GET /v1/users/profile/me`).
     *
     * `users/profile` is the COMPANY profile (`company_name`, `industry`, `brand_voice`) and
     * carries no personal name, email or phone. Until this route existed, the owner and
     * organization admin were the only actors with no way to maintain their own contact details
     * while every manager and team member had one, which is why the assistant could not reach the
     * person most likely to receive an escalation (workspace ADR 0016 phase 1b).
     *
     * Returns the same field set as the member and staff profiles so one component serves all
     * three roles. `email` is read-only there and here: it is the login identity.
     */
    getOwnPersonalProfile(): Observable<OwnerPersonalProfile> {
        return this._httpClient
            .get<{ data: { user: OwnerPersonalProfile } }>(this.apiLink + 'users/profile/me')
            .pipe(map(response => response.data.user));
    }

    /**
     * Update the owner's own details.
     *
     * Deliberately a separate call from `editcompanyData`: widening the company payload would mean
     * a personal-details save also submits the brand voice and the headquarters address, and one
     * validation change to either form would break the other.
     */
    updateOwnPersonalProfile(
        body: Partial<Pick<OwnerPersonalProfile, 'name' | 'phone' | 'job_description' | 'image' | 'time_zone'>>,
    ): Observable<OwnerPersonalProfile> {
        return this._httpClient
            .put<{ data: { user: OwnerPersonalProfile } }>(this.apiLink + 'users/profile/me', body)
            .pipe(
                map(response => response.data.user),
                tap((user) => {
                    if (user?.name) {
                        localStorage.setItem('user_name', user.name);
                    }
                }),
            );
    }

    /** Member/staff personal avatar stream (seeded from storage; updated on profile picture change). */
    get memberStaffPhoto$(): Observable<string> {
        return this._memberStaffPhoto.asObservable();
    }

    /** Cached member/staff profile stream (populated by `loadMemberStaffProfile`). */
    get memberStaffProfile$(): Observable<MemberStaffProfile | null> {
        return this._memberStaffProfile.asObservable();
    }

    /** Whether the stored role is a member (`manager`) or staff actor. */
    private isMemberStaffRole(): boolean {
        const role = localStorage.getItem('userRole') ?? '';
        return role === 'manager' || role === 'staff';
    }

    /** Resolve the member/staff self-service profile base path from the stored role. */
    private memberStaffProfilePath(): string {
        return localStorage.getItem('userRole') === 'staff'
            ? 'staff/profile'
            : 'members/profile';
    }

    /** Push member/staff profile fields into in-memory subjects and `localStorage`. */
    private applyMemberStaffProfile(profile: MemberStaffProfile): void {
        this._memberStaffProfile.next(profile);
        const image = normalizeAvatarUrl(profile?.image);
        this._memberStaffPhoto.next(image);
        if (profile.email) {
            localStorage.setItem('email', profile.email);
        }
        if (profile.name) {
            localStorage.setItem('user_name', profile.name);
        }
        if (image) {
            localStorage.setItem('user_image', image);
        } else {
            localStorage.removeItem('user_image');
        }
        this._languagePreferenceService.applyFromBackend(profile?.prefLang);
    }

    /**
     * Eagerly load member/staff profile (mirrors `getcompany()` for company owners).
     * Returns the cached profile when already fetched this session; otherwise hits the API.
     */
    loadMemberStaffProfile(): Observable<MemberStaffProfile> {
        if (!this.isMemberStaffRole()) {
            return throwError(() => new Error('loadMemberStaffProfile requires manager or staff role'));
        }
        const cached = this._memberStaffProfile.value;
        if (cached) {
            return of(cached);
        }
        return this.getMyProfile();
    }

    /** Fetch the authenticated member/staff personal profile (name, read-only email, departments, job description, picture). */
    getMyProfile(): Observable<MemberStaffProfile> {
        return this._httpClient
            .get<{ data: { member?: MemberStaffProfile; staff?: MemberStaffProfile } }>(
                this.apiLink + this.memberStaffProfilePath()
            )
            .pipe(
                map((response) => {
                    const profile = response.data.member ?? response.data.staff;
                    return profile as MemberStaffProfile;
                }),
                tap(profile => this.applyMemberStaffProfile(profile))
            );
    }

    /** Update the authenticated member/staff profile (name/job description/picture). Email is read-only. */
    updateMyProfile(body: MemberStaffProfileUpdate): Observable<MemberStaffProfile> {
        return this._httpClient
            .put<{ data: { member?: MemberStaffProfile; staff?: MemberStaffProfile } }>(
                this.apiLink + this.memberStaffProfilePath(),
                body
            )
            .pipe(
                map((response) => {
                    const profile = response.data.member ?? response.data.staff;
                    return profile as MemberStaffProfile;
                }),
                tap(profile => this.applyMemberStaffProfile(profile))
            );
    }

    sendOTPToDeleteAccount() {
        return this._httpClient.post(
            this.apiLink + 'users/delete-account-otp',
            {}
        );
    }

    deleteAccount(modal) {
        return this._httpClient.post(
            this.apiLink + 'users/delete-account',
            modal
        );
    }

    editLogo(logo: string) {
        return this._httpClient
            .put<any>(this.apiLink + 'users/profile/logo', { image: logo })
            .pipe(
                map((response) => {
                    this.clearCompanyProfileCache();
                    this._company.next(response.data.user);
                    this._user.next(response.data.user);
                    return of(response.data.user);
                })
            );
    }

    /**
     * Fetches the organization logo as a base64 data URL via backend proxy.
     * This avoids CORS restrictions when the logo is hosted on external storage (e.g. S3).
     */
    getLogoDataUrl(): Observable<string | null> {
        return this._httpClient
            .get<any>(this.apiLink + 'users/profile/logo-data')
            .pipe(map(response => response?.data?.imageData ?? null));
    }

    /** Alias for `getcompany()` — same shared `GET users/profile` stream. */
    getUser(): Observable<Company> {
        return this.getcompany();
    }

    update(user: Company): Observable<any> {
        return this._httpClient
            .patch<Company>('api/common/user', { user })
            .pipe(
                map((response) => {
                    this._user.next(response);
                })
            );
    }
}
